import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { entryTypesService } from '../services/entryTypes.service';
import {
  EntryTypeDto,
  EntryTypeCategory,
  EntryTypeCategoryLabels
} from '../types/entryTypes.types';

const EntryTypesPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const [entryTypes, setEntryTypes] = useState<EntryTypeDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showManualOnly, setShowManualOnly] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [includeInactive, showManualOnly]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      let data: EntryTypeDto[];
      if (showManualOnly) {
        data = await entryTypesService.getManualEntryTypes(includeInactive);
      } else {
        data = await entryTypesService.getAll(includeInactive);
      }
      
      setEntryTypes(data);
    } catch (err: any) {
      const errorMsg = isRTL ? 'فشل تحميل أنواع القيود' : 'Failed to load entry types';
      setError(err.response?.data?.message || errorMsg);
      console.error('Failed to load entry types:', err);
    } finally {
      setLoading(false);
    }
  };

  // Group entry types by category
  const groupedEntryTypes = entryTypes.reduce((acc, et) => {
    const category = et.category as EntryTypeCategory;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(et);
    return acc;
  }, {} as Record<EntryTypeCategory, EntryTypeDto[]>);

  // Calculate statistics
  const stats = {
    total: entryTypes.length,
    active: entryTypes.filter(et => et.isActive).length,
    manual: entryTypes.filter(et => et.allowManualEntry).length,
    requiresApproval: entryTypes.filter(et => et.requiresApproval).length
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" component="h1" sx={{ mb: 1 }}>
            {isRTL ? 'أنواع القيود' : 'Entry Types'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isRTL 
              ? 'عرض جميع أنواع القيود المحاسبية في النظام (للقراءة فقط)'
              : 'View all accounting entry types in the system (Read-only)'
            }
          </Typography>
        </Box>

        {/* Statistics Cards */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 3 
        }}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                {isRTL ? 'إجمالي الأنواع' : 'Total Types'}
              </Typography>
              <Typography variant="h4" component="div">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
          
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                {isRTL ? 'الأنواع النشطة' : 'Active Types'}
              </Typography>
              <Typography variant="h4" component="div" color="success.main">
                {stats.active}
              </Typography>
            </CardContent>
          </Card>
          
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                {isRTL ? 'قيود يدوية' : 'Manual Entries'}
              </Typography>
              <Typography variant="h4" component="div" color="primary.main">
                {stats.manual}
              </Typography>
            </CardContent>
          </Card>
          
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                {isRTL ? 'تتطلب موافقة' : 'Require Approval'}
              </Typography>
              <Typography variant="h4" component="div" color="info.main">
                {stats.requiresApproval}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Filters */}
        <Box sx={{ mb: 3, display: 'flex', gap: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
            }
            label={isRTL ? 'إظهار غير النشط' : 'Show Inactive'}
          />
          <FormControlLabel
            control={
              <Switch
                checked={showManualOnly}
                onChange={(e) => setShowManualOnly(e.target.checked)}
              />
            }
            label={isRTL ? 'القيود اليدوية فقط' : 'Manual Entries Only'}
          />
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Content */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress />
          </Box>
        ) : entryTypes.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography color="text.secondary">
              {isRTL ? 'لا توجد بيانات' : 'No data available'}
            </Typography>
          </Box>
        ) : (
          <Box>
            {/* Grouped by Category */}
            {Object.entries(groupedEntryTypes).map(([category, types]) => (
              <Accordion key={category} defaultExpanded>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ backgroundColor: 'action.hover' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <DescriptionIcon color="primary" />
                    <Typography variant="h6">
                      {isRTL
                        ? (EntryTypeCategoryLabels[category as EntryTypeCategory]?.ar ?? category)
                        : (EntryTypeCategoryLabels[category as EntryTypeCategory]?.en ?? category)
                      }
                    </Typography>
                    <Chip 
                      label={types.length} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{isRTL ? 'الرمز' : 'Code'}</TableCell>
                          <TableCell>{isRTL ? 'البادئة' : 'Prefix'}</TableCell>
                          <TableCell>{isRTL ? 'الاسم' : 'Name'}</TableCell>
                          <TableCell align="center">{isRTL ? 'يدوي' : 'Manual'}</TableCell>
                          <TableCell align="center">{isRTL ? 'موافقة' : 'Approval'}</TableCell>
                          <TableCell align="center">{isRTL ? 'الحالة' : 'Status'}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {types.map((et) => (
                          <TableRow key={et.entryTypeId} hover>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                              >
                                {et.entryTypeCode}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={et.prefix} 
                                size="small" 
                                variant="outlined"
                                sx={{ fontFamily: 'monospace' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {isRTL ? et.entryTypeNameAr : et.entryTypeName}
                              </Typography>
                              {et.description && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {et.description}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {et.allowManualEntry ? (
                                <CheckCircleIcon color="success" fontSize="small" />
                              ) : (
                                <CancelIcon color="disabled" fontSize="small" />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {et.requiresApproval ? (
                                <CheckCircleIcon color="info" fontSize="small" />
                              ) : (
                                <CancelIcon color="disabled" fontSize="small" />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={et.isActive 
                                  ? (isRTL ? 'نشط' : 'Active')
                                  : (isRTL ? 'غير نشط' : 'Inactive')
                                }
                                size="small"
                                color={et.isActive ? 'success' : 'error'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default EntryTypesPage;